import Cookies from 'js-cookie';

// Cookie Types
export enum CookieCategory {
  ESSENTIAL = 'necessary',
  ANALYTICS = 'analytics',
  MARKETING = 'marketing'
}

export interface CookieOptions {
  expires?: number; // Days
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean; // Note: Can only be set by server
}

// Default secure cookie options
const defaultOptions: CookieOptions = {
  path: '/',
  secure: window.location.protocol === 'https:',
  sameSite: 'strict',
  expires: 30 // 30 days
};

// Essential session cookie options
const sessionOptions: CookieOptions = {
  ...defaultOptions,
  sameSite: 'lax', // Allow cookies in navigation requests
  expires: 1 // 1 day for session
};

// Check if consent has been given for a specific category
export const hasConsent = (category: CookieCategory): boolean => {
  if (category === CookieCategory.ESSENTIAL) {
    return true; // Essential cookies always have consent
  }

  // Check for specific category consent
  const categoryConsent = Cookies.get(`${category}_consent`);
  if (categoryConsent === 'true') {
    return true;
  }

  // Check general consent level
  const consentLevel = Cookies.get('consent_level');
  if (consentLevel === 'full') {
    return true;
  }

  // Check localStorage (for backward compatibility)
  try {
    const storedConsent = localStorage.getItem('cookieConsent');
    if (storedConsent) {
      const consentData = JSON.parse(storedConsent);
      if (consentData.categories?.[category]?.enabled === true) {
        return true;
      }
    }
  } catch (error) {
    console.error('Error checking localStorage consent:', error);
  }

  return false;
};

// Get a cookie if consent is given for its category
export const getCookie = (name: string, category: CookieCategory = CookieCategory.ESSENTIAL): string | undefined => {
  if (!hasConsent(category)) {
    console.warn(`Cannot access ${name} cookie: no consent for ${category} category`);
    return undefined;
  }
  return Cookies.get(name);
};

// Set a cookie if consent is given for its category
export const setCookie = (name: string, value: string, category: CookieCategory = CookieCategory.ESSENTIAL, options?: CookieOptions): void => {
  if (!hasConsent(category) && category !== CookieCategory.ESSENTIAL) {
    console.warn(`Cannot set ${name} cookie: no consent for ${category} category`);
    return;
  }

  // Merge default options with provided options
  const cookieOptions = { ...defaultOptions, ...options };
  Cookies.set(name, value, cookieOptions);
};

// Remove a cookie
export const removeCookie = (name: string, options?: CookieOptions): void => {
  Cookies.remove(name, { ...defaultOptions, ...options });
};

// Set a cart cookie with cart data
export const setCartCookie = (cartData: any): void => {
  try {
    const cartString = JSON.stringify(cartData);
    setCookie('cart', cartString, CookieCategory.ESSENTIAL, {
      ...defaultOptions,
      expires: 7 // Cart cookies expire in 1 week
    });
  } catch (error) {
    console.error('Error setting cart cookie:', error);
  }
};

// Get cart data from cookie
export const getCartFromCookie = (): any => {
  try {
    const cartString = getCookie('cart');
    if (!cartString) return null;
    return JSON.parse(cartString);
  } catch (error) {
    console.error('Error parsing cart cookie:', error);
    return null;
  }
};

// Clear all non-essential cookies
export const clearNonEssentialCookies = (): void => {
  // Get all cookies
  const cookies = document.cookie.split(';');
  
  // List of essential cookies that should not be deleted
  const essentialCookies = ['cart', 'session', 'cookieconsent_status', 'cookie_terms_accepted', 'consent_level'];
  
  // Iterate through cookies and remove non-essential ones
  cookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    
    // Skip essential cookies
    if (essentialCookies.includes(cookieName)) {
      return;
    }
    
    // Remove GA cookies
    if (cookieName.startsWith('_ga') || cookieName.startsWith('_gid') || cookieName.startsWith('_gat')) {
      removeCookie(cookieName);
    }
    
    // Remove other known non-essential cookies
    if (cookieName.includes('analytics') || cookieName.includes('marketing')) {
      removeCookie(cookieName);
    }
  });
};

export default {
  getCookie,
  setCookie,
  removeCookie,
  hasConsent,
  setCartCookie,
  getCartFromCookie,
  clearNonEssentialCookies
}; 